type PublicRuntimeConfig = {
  apiBaseUrl?: string;
  wsBaseUrl?: string;
  providerId?: string;
  tenantId?: string;
  sector?: string;
  accessToken?: string;
  access_token?: string;
  token?: string;
  apiProfile?: string;
  apiKey?: string;
  requireHostInit?: string | boolean;
  title?: string;
  subtitle?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
};

const DEFAULT_XEDU_SECTOR = 'xedu';

export type EmbedInitRuntimeConfig = {
  tenantId?: string;
  providerId?: string;
  accessToken?: string;
  sessionExists?: boolean;
};

export type PublicBrandingRuntimeConfig = {
  title?: string;
  subtitle?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
};

declare global {
  interface Window {
    __XEDU_PUBLIC_CONFIG__?: PublicRuntimeConfig;
  }
}

function normalizeBaseUrl(value: string): string {
  const trimmed = value.trim();
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

function isViteHtmlPlaceholder(value: string): boolean {
  return value.includes('%VITE_') && value.includes('%');
}

function readGlobalConfig(): PublicRuntimeConfig | undefined {
  try {
    if (typeof window !== 'undefined' && window.__XEDU_PUBLIC_CONFIG__) {
      return window.__XEDU_PUBLIC_CONFIG__;
    }
  } catch {
    // ignore
  }
  return undefined;
}

function isUuidLike(value: string): boolean {
  const s = (value || '').trim();
  if (!s) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

function readQueryConfig(): EmbedInitRuntimeConfig | undefined {
  try {
    if (typeof window === 'undefined' || !window.location?.search) return undefined;

    const params = new URLSearchParams(window.location.search);
    const read = (...keys: string[]): string | undefined => {
      for (const key of keys) {
        const value = params.get(key);
        if (value && value.trim()) return value.trim();
      }
      return undefined;
    };

    const rawSessionExists = read('sessionExists', 'session_exists');
    const sessionExists = rawSessionExists
      ? ['1', 'true', 'yes', 'on'].includes(rawSessionExists.toLowerCase())
      : undefined;

    const cfg: EmbedInitRuntimeConfig = {
      tenantId: read('tenantId', 'tenant_id', 'tenant'),
      providerId: read('providerId', 'provider_id', 'provider'),
      accessToken: read('accessToken', 'access_token', 'token'),
      sessionExists,
    };

    if (!cfg.tenantId && !cfg.providerId && !cfg.accessToken && cfg.sessionExists === undefined) {
      return undefined;
    }

    return cfg;
  } catch {
    return undefined;
  }
}

function readQueryValue(...keys: string[]): string | undefined {
  try {
    if (typeof window === 'undefined' || !window.location?.search) return undefined;
    const params = new URLSearchParams(window.location.search);
    for (const key of keys) {
      const value = params.get(key);
      if (value && value.trim()) return value.trim();
    }
  } catch {
    // ignore
  }
  return undefined;
}

function readEnv(name: string): string | undefined {
  // NOTE: Do NOT use `import.meta` here.
  // Expo/Metro web serves bundles as classic scripts, and `import.meta` is a
  // SyntaxError outside ES modules.

  // Expo / Node-style env shims
  try {
    const proc = (globalThis as any)?.process;
    const value = proc?.env?.[name];
    if (typeof value === 'string' && value.trim()) return value.trim();
  } catch {
    // ignore
  }

  return undefined;
}

function isLocalBrowserHost(): boolean {
  try {
    if (typeof window === 'undefined' || !window.location) return false;
    const host = window.location.hostname;
    return host === 'localhost' || host === '0.0.0.0';
  } catch {
    return false;
  }
}

function isFrontendDevServerPort(port: string): boolean {
  return ['19006', '3000', '4173', '5173', '8081', '8087'].includes((port || '').trim());
}

export function getApiBaseUrl(): string {
  const global = readGlobalConfig();
  const fromGlobal = global?.apiBaseUrl;
  if (fromGlobal && fromGlobal.trim() && !isViteHtmlPlaceholder(fromGlobal)) {
    return normalizeBaseUrl(fromGlobal);
  }

  const fromEnv =
    readEnv('VITE_API_URL') ||
    readEnv('VITE_API_BASE_URL') ||
    readEnv('EXPO_PUBLIC_API_URL');
  if (fromEnv) return normalizeBaseUrl(fromEnv);

  // Default: same-origin requests. Deployed chat hosts should talk to
  // xedu_ai backend through their own origin unless cross-origin API traffic is
  // explicitly configured.
  // Dev fallback: Expo web / Metro has no proxy, so same-origin returns HTML.
  try {
    if (typeof window !== 'undefined' && window.location) {
      if (isLocalBrowserHost() && isFrontendDevServerPort(window.location.port || '')) {
        return `${window.location.protocol}//${window.location.hostname}:8007`;
      }
    }
  } catch {
    // ignore
  }

  return '';
}

export function getApiProfile(): 'default' | 'adhd_external' {
  // XEdu does not expose the ADHD external app profile.
  return 'default';
}

export function getSector(): string {
  const fromQuery = readQueryValue('xedu_sector', 'sector');
  if (fromQuery && !isViteHtmlPlaceholder(fromQuery)) return normalizeSector(fromQuery);

  const global = readGlobalConfig();
  const fromGlobal = global?.sector;
  if (fromGlobal && fromGlobal.trim() && !isViteHtmlPlaceholder(fromGlobal)) return normalizeSector(fromGlobal);

  const fromEnv = readEnv('VITE_SECTOR') || readEnv('EXPO_PUBLIC_SECTOR');
  if (fromEnv && fromEnv.trim()) return normalizeSector(fromEnv);

  return DEFAULT_XEDU_SECTOR;
}

function normalizeSector(value: string): string {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'education' || normalized === 'edu') return 'xedu';
  return normalized || DEFAULT_XEDU_SECTOR;
}

export function getApiKey(): string {
  const fromQuery = readQueryValue('xedu_api_key', 'apiKey', 'api_key', 'x_api_key');
  if (fromQuery) return fromQuery;

  const global = readGlobalConfig();
  const fromGlobal = global?.apiKey;
  if (fromGlobal && fromGlobal.trim() && !isViteHtmlPlaceholder(fromGlobal)) {
    return fromGlobal.trim();
  }

  const fromEnv = readEnv('VITE_API_KEY') || readEnv('EXPO_PUBLIC_API_KEY');
  return fromEnv ? fromEnv.trim() : '';
}

export function getBrandingRuntimeConfig(): PublicBrandingRuntimeConfig {
  const global = readGlobalConfig();

  const title = readQueryValue('xedu_title', 'title') || global?.title;
  const subtitle = readQueryValue('xedu_subtitle', 'subtitle') || global?.subtitle;
  const primaryColor = readQueryValue('xedu_primary_color', 'primaryColor', 'primary_color') || global?.primaryColor;
  const secondaryColor = readQueryValue('xedu_secondary_color', 'secondaryColor', 'secondary_color') || global?.secondaryColor;
  const accentColor = readQueryValue('xedu_accent_color', 'accentColor', 'accent_color') || global?.accentColor;

  return {
    title: title && !isViteHtmlPlaceholder(title) ? title : undefined,
    subtitle: subtitle && !isViteHtmlPlaceholder(subtitle) ? subtitle : undefined,
    primaryColor: primaryColor && !isViteHtmlPlaceholder(primaryColor) ? primaryColor : undefined,
    secondaryColor: secondaryColor && !isViteHtmlPlaceholder(secondaryColor) ? secondaryColor : undefined,
    accentColor: accentColor && !isViteHtmlPlaceholder(accentColor) ? accentColor : undefined,
  };
}

export function getRequireHostInitOverride(): boolean | undefined {
  const fromQuery = readQueryValue('xedu_require_host_init', 'requireHostInit', 'require_host_init');
  const normalizedQuery = (fromQuery || '').trim().toLowerCase();
  if (normalizedQuery) {
    if (['1', 'true', 'yes', 'on'].includes(normalizedQuery)) return true;
    if (['0', 'false', 'no', 'off'].includes(normalizedQuery)) return false;
  }

  const global = readGlobalConfig();
  const rawGlobal = global?.requireHostInit;
  if (typeof rawGlobal === 'boolean') return rawGlobal;
  if (typeof rawGlobal === 'string') {
    const normalizedGlobal = rawGlobal.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(normalizedGlobal)) return true;
    if (['0', 'false', 'no', 'off'].includes(normalizedGlobal)) return false;
  }

  return undefined;
}

export function getWsBaseUrl(): string {
  const global = readGlobalConfig();
  const fromGlobal = global?.wsBaseUrl;
  if (fromGlobal && fromGlobal.trim() && !isViteHtmlPlaceholder(fromGlobal)) {
    return normalizeBaseUrl(fromGlobal);
  }

  const fromEnv =
    readEnv('VITE_WS_URL') ||
    readEnv('VITE_WS_BASE_URL') ||
    readEnv('EXPO_PUBLIC_WS_URL');
  if (fromEnv) return normalizeBaseUrl(fromEnv);

  // Default: same-origin WS on the chat host for xedu_ai.
  try {
    if (typeof window !== 'undefined' && window.location) {
      if (isLocalBrowserHost() && isFrontendDevServerPort(window.location.port || '')) {
        return `ws://${window.location.hostname}:8007`;
      }
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}`;
    }
  } catch {
    // ignore
  }

  return 'ws://localhost:8007';
}

export function getProviderId(): string {
  const global = readGlobalConfig();
  const fromGlobal = global?.providerId;
  if (fromGlobal && fromGlobal.trim() && !isViteHtmlPlaceholder(fromGlobal) && isUuidLike(fromGlobal)) {
    return fromGlobal.trim();
  }

  const query = readQueryConfig();
  if (query?.providerId && isUuidLike(query.providerId)) {
    return query.providerId.trim();
  }
  if (query?.tenantId && isUuidLike(query.tenantId)) {
    return query.tenantId.trim();
  }

  // Prefer tenantId (UUID) from the host/main-app when providerId isn't explicitly set.
  // This avoids hardcoding non-UUID tenant slugs into API calls.
  const tenantFromGlobal = global?.tenantId;
  if (
    tenantFromGlobal &&
    tenantFromGlobal.trim() &&
    !isViteHtmlPlaceholder(tenantFromGlobal) &&
    isUuidLike(tenantFromGlobal)
  ) {
    return tenantFromGlobal.trim();
  }

  const fromEnv =
    readEnv('VITE_PROVIDER_ID') ||
    readEnv('EXPO_PUBLIC_PROVIDER_ID') ||
    readEnv('VITE_TENANT_ID') ||
    readEnv('EXPO_PUBLIC_TENANT_ID') ||
    '';

  return isUuidLike(fromEnv) ? fromEnv.trim() : '';
}

export function getEmbedInitFromRuntime(): EmbedInitRuntimeConfig | null {
  const global = readGlobalConfig();
  const query = readQueryConfig();
  const globalAccessToken =
    typeof global?.accessToken === 'string' && global.accessToken.trim()
      ? global.accessToken.trim()
      : typeof global?.access_token === 'string' && global.access_token.trim()
        ? global.access_token.trim()
        : typeof global?.token === 'string' && global.token.trim()
          ? global.token.trim()
          : undefined;

  if (!query && !globalAccessToken) return null;

  return {
    tenantId: query?.tenantId && isUuidLike(query.tenantId) ? query.tenantId : undefined,
    providerId: query?.providerId && isUuidLike(query.providerId) ? query.providerId : undefined,
    accessToken: query?.accessToken || globalAccessToken,
    sessionExists: query?.sessionExists,
  };
}
